import { IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { Store } from '@ngxs/store';
import _ from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { ChangeReaderToEditor } from 'src/app/store/samwin/email/email-dialog/email-dialog-behaviour.actions';
import { StartQueueSelectionProcess } from 'src/app/store/samwin/queue-selection-dialog/queue-selection-dialog.actions';
import { MessageInfo } from 'src/app/store/samwin/samwin-mail-list/message-info';
import {
   CloseMessage,
   MoveMessage,
   ParkMessageOnAgent,
   ParkMessageOnSkill,
   RemoveFromMessages,
} from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.actions';
import { SamwinMessageListState } from 'src/app/store/samwin/samwin-mail-list/samwin-message-list.state';
import { SamwinMessageLinesState } from 'src/app/store/samwin/samwin-message-lines/samwin-message-lines.state';
import { MailingFolderEnum } from 'src/app/store/samwin/samwin-skills/mailing-folders-enum';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { Enum, EnumType } from 'ts-jenum';
import { EmailPurpose } from '../../samwin/EmailPurpose';
import { FeatureEnum } from '../../samwin/FeatureEnum';
import { QueueSelectionPurpose } from '../../samwin/QueueSelectionPurpose';

@Enum<MessageActions>('value')
export class MessageActions extends EnumType<MessageActions>() {
   static readonly UNDO = new MessageActions(
      'UNDO',
      'messages_actions.undo',
      WellKnownIconsEnum.EMAILING_UNDO.icon,
      null,
      (store: Store, messageInfo: MessageInfo) => {
         return MessageActions.moveAction(MailingFolderEnum.INBOX.value, store, messageInfo);
      },
      (store) => store.select(SamwinMessageLinesState.isEmailingEnabled)
   );
   static readonly REPLY = new MessageActions(
      'REPLY',
      'messages_actions.reply',
      WellKnownIconsEnum.EMAILING_REPLY.icon,
      null,
      (store: Store, messageInfo: MessageInfo) =>
         store.dispatch(
            new ChangeReaderToEditor(
               messageInfo.messageId,
               messageInfo.skill.skillId,
               EmailPurpose.REPLY,
               store.selectSnapshot(SamwinMessageListState.getWaitListItemById)(messageInfo.messageId)
            )
         ),
      (store, messageInfo: MessageInfo) => {
         const isFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA));
         const hasConnector$ = store.select(SamwinSkillsState.hasCurrentUserEmailConnector);
         return combineLatest([isFeatureEnabled$, hasConnector$]).pipe(map(([isEnabled, hasConnector]) => isEnabled && hasConnector(messageInfo.queueId)));
      }
   );
   static readonly REPLY_ALL = new MessageActions(
      'REPLY_ALL',
      'messages_actions.reply_all',
      WellKnownIconsEnum.EMAILING_REPLY_ALL.icon,
      null,
      (store: Store, messageInfo: MessageInfo) =>
         store.dispatch(
            new ChangeReaderToEditor(
               messageInfo.messageId,
               messageInfo.skill.skillId,
               EmailPurpose.REPLY_ALL,
               store.selectSnapshot(SamwinMessageListState.getWaitListItemById)(messageInfo.messageId)
            )
         ),
      (store, messageInfo: MessageInfo) => {
         const isFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA));
         const hasConnector$ = store.select(SamwinSkillsState.hasCurrentUserEmailConnector);
         return combineLatest([isFeatureEnabled$, hasConnector$]).pipe(map(([isEnabled, hasConnector]) => isEnabled && hasConnector(messageInfo.queueId)));
      }
   );
   static readonly FORWARD = new MessageActions(
      'FORWARD',
      'messages_actions.forward',
      WellKnownIconsEnum.EMAILING_FORWARD.icon,
      null,
      (store: Store, messageInfo: MessageInfo) =>
         store.dispatch(
            new ChangeReaderToEditor(
               messageInfo.messageId,
               messageInfo.skill.skillId,
               EmailPurpose.FORWARD,
               store.selectSnapshot(SamwinMessageListState.getWaitListItemById)(messageInfo.messageId)
            )
         ),
      (store, messageInfo: MessageInfo) => {
         const isFeatureEnabled$ = store.select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_SOCIAL_MEDIA));
         const hasConnector$ = store.select(SamwinSkillsState.hasCurrentUserEmailConnector);
         return combineLatest([isFeatureEnabled$, hasConnector$]).pipe(map(([isEnabled, hasConnector]) => isEnabled && hasConnector(messageInfo.queueId)));
      }
   );
   static readonly PARK_AGENT = new MessageActions(
      'PARK_AGENT',
      'messages_actions.park_agent',
      WellKnownIconsEnum.EMAILING_PARK_AGENT.icon,
      WellKnownIconsEnum.EMAILING_PARK_AGENT.iconSmall,
      (store: Store, messageInfo: MessageInfo) => {
         store.dispatch(new ParkMessageOnAgent(messageInfo.callId, messageInfo.messageId));
         store.dispatch(new RemoveFromMessages(messageInfo.messageId));
         return of(true);
      },
      (store) => store.select(SamwinMessageLinesState.canMessageBeParkedAtAgent)
   );
   static readonly PARK_SKILL = new MessageActions(
      'PARK_SKILL',
      'messages_actions.park_skill',
      WellKnownIconsEnum.EMAILING_PARK_SKILL.icon,
      WellKnownIconsEnum.EMAILING_PARK_SKILL.iconSmall,
      (store: Store, messageInfo: MessageInfo) => {
         store.dispatch(new ParkMessageOnSkill(messageInfo.callId, messageInfo.skill.skillId, messageInfo.messageId));
         store.dispatch(new RemoveFromMessages(messageInfo.messageId));
         return of(true);
      },
      (store) => store.select(SamwinMessageLinesState.canMessageBeParkedAtSkill)
   );
   static readonly REQUEUE = new MessageActions(
      'REQUEUE',
      'messages_actions.requeue',
      WellKnownIconsEnum.EMAILING_REQUEUE.icon,
      null,
      (store: Store, messageInfo: MessageInfo) =>
         store.dispatch(
            new StartQueueSelectionProcess(
               QueueSelectionPurpose.REQUEUE,
               store.selectSnapshot(SamwinMessageListState.getWaitListItemById)(messageInfo.messageId),
               messageInfo.skill.skillId
            )
         ),
      (store) => store.select(SamwinMessageLinesState.isEmailingEnabled)
   );
   static readonly TRACK = new MessageActions(
      'TRACK',
      'messages_actions.track',
      WellKnownIconsEnum.EMAILING_TRACK.icon,
      null,
      (store: Store, messageInfo: MessageInfo) => {
         return MessageActions.moveAction(MailingFolderEnum.TRACKING.value, store, messageInfo);
      },
      (store) => store.select(SamwinMessageLinesState.isEmailingEnabled)
   );
   static readonly COMPLETE = new MessageActions(
      'COMPLETE',
      'messages_actions.complete',
      WellKnownIconsEnum.EMAILING_COMPLETE.icon,
      null,
      (store: Store, messageInfo: MessageInfo) => {
         return MessageActions.moveAction(MailingFolderEnum.COMPLETED.value, store, messageInfo);
      },
      (store) => store.select(SamwinMessageLinesState.isEmailingEnabled)
   );
   static readonly DELETE = new MessageActions(
      'DELETE',
      'messages_actions.delete',
      WellKnownIconsEnum.EMAILING_DELETE.icon,
      null,
      (store: Store, messageInfo: MessageInfo) => {
         return MessageActions.moveAction(MailingFolderEnum.DELETED.value, store, messageInfo);
      },
      (store) => store.select(SamwinMessageLinesState.isEmailingEnabled)
   );

   private static moveAction(target: number, store: Store, messageInfo: MessageInfo): Observable<boolean> {
      if (messageInfo.group !== MailingFolderEnum.INBOX.name) {
         store.dispatch(new MoveMessage(messageInfo.messageId, target, messageInfo.skill.skillId));
      } else {
         store.dispatch(new CloseMessage(messageInfo.callId, messageInfo.messageId, target));
      }
      store.dispatch(new RemoveFromMessages(messageInfo.messageId));
      return of(true);
   }

   static getValues(): MessageActions[] {
      return _(MessageActions.values()).value();
   }

   constructor(
      public value,
      public translationValue,
      public defaultIcon: IconDefinition,
      public subIcon: IconDefinition,
      private executionAction$: (store: Store, messageInfo: MessageInfo) => Observable<any>,
      public isActionExecutable$: (store: Store, messageInfo: MessageInfo) => Observable<boolean>,
      public isActionToggled$?: (store: Store) => Observable<boolean>
   ) {
      super();
   }

   public executeAction$(store: Store, messageInfo: MessageInfo): Observable<any> {
      const result = this.executionAction$(store, messageInfo);
      return result;
   }
}
